/**
 * LOGOI LAB - Monte Carlo Simulation Engine
 * 10,000 Stochastic Trajectories for Consciousness Evolution
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { ConsciousnessState, MonteCarloResult, MonteCarloStats } from '../types';
import { clamp, percentile, mean, std, wilsonCI, gaussianRandom } from '../utils/math';
import { computeLogosAlignment } from '../core/logos';
import { computeViability, computeEntropy, computeOmega } from '../core/derived';
import { computeCoherence } from '../core/penrose';
import { computeFreeEnergy } from '../core/friston';
import { inhibitionPolicy } from '../core/policy';

/**
 * Project state stochastically with Gaussian noise
 */
function projectStateStochastic(s: ConsciousnessState, sigma: number): ConsciousnessState {
  const next: Record<string, number> = {};
  
  for (const k of Object.keys(s)) {
    const gauss = gaussianRandom();
    const currentVal = (s as any)[k];
    const drift = (currentVal > 0.5 ? -0.002 : 0.003) + (Math.random() - 0.48) * 0.005;
    next[k] = clamp(currentVal + drift + sigma * gauss);
  }
  
  return next as ConsciousnessState;
}

/**
 * Run Monte Carlo Simulation
 * 
 * Simulates N stochastic trajectories of consciousness evolution
 * over `steps` time periods, with Gaussian noise of standard deviation `sigma`.
 * 
 * @param s - Initial consciousness state
 * @param N - Number of simulations (default 10000)
 * @param steps - Number of time steps per simulation (default 30)
 * @param sigma - Standard deviation of Gaussian noise (default 0.04)
 * @returns MonteCarloResult with statistics and confidence intervals
 */
export function runMonteCarlo(
  s: ConsciousnessState, 
  N: number = 10000, 
  steps: number = 30, 
  sigma: number = 0.04
): MonteCarloResult {
  // Initialize tracking arrays
  const LamByT: number[][] = Array.from({ length: steps + 1 }, () => []);
  const VbyT: number[][] = Array.from({ length: steps + 1 }, () => []);
  const SbyT: number[][] = Array.from({ length: steps + 1 }, () => []);
  const OmegaByT: number[][] = Array.from({ length: steps + 1 }, () => []);
  
  // π verdict tracking: [PAUSA, ESPERA, RECONECTA, MONITOREA, ACTÚA]
  const verdictByT: number[][] = Array.from({ length: steps + 1 }, () => [0, 0, 0, 0, 0]);
  const verdictNames = ["PAUSA", "ESPERA", "RECONECTA", "MONITOREA", "ACTÚA"];
  const verdictColors = ["#ef4444", "#f59e0b", "#a855f7", "#6366f1", "#10b981"];
  
  const timeToActua: number[] = [];
  const allFinalLambda: number[] = [];
  let negativeAttractorCount = 0;
  
  // Run N simulations
  for (let i = 0; i < N; i++) {
    let cur = { ...s };
    let firstActuaStep = -1;
    let prevLambda = computeLogosAlignment(cur).value;
    
    for (let t = 0; t <= steps; t++) {
      const lam = computeLogosAlignment(cur).value;
      const vObj = computeViability(cur, lam);
      const sObj = computeEntropy(cur, lam);
      const cObj = computeCoherence(cur, lam);
      const fObj = computeFreeEnergy(cur, lam);
      const omObj = computeOmega(lam, vObj.value, sObj.value, cObj.coherence, fObj.value);
      
      // Record values
      LamByT[t].push(lam);
      VbyT[t].push(vObj.value);
      SbyT[t].push(sObj.value);
      OmegaByT[t].push(omObj.value);
      
      // Evaluate π verdict
      const pi = inhibitionPolicy(lam, vObj.value, sObj.value, cObj.coherence, prevLambda);
      let vCode: number;
      switch (pi.verdict) {
        case "PAUSA": vCode = 0; break;
        case "ESPERA": vCode = 1; break;
        case "RECONECTA": vCode = 2; break;
        case "MONITOREA": vCode = 3; break;
        default: vCode = 4; // ACTÚA
      }
      verdictByT[t][vCode]++;
      
      if (vCode === 4 && firstActuaStep === -1) {
        firstActuaStep = t;
      }
      
      if (t === steps) {
        allFinalLambda.push(lam);
        if (lam < 0.20 && sObj.value > 0.50) {
          negativeAttractorCount++;
        }
      } else {
        prevLambda = lam;
        cur = projectStateStochastic(cur, sigma);
      }
    }
    
    timeToActua.push(firstActuaStep);
  }
  
  // Compute statistics
  const stats: MonteCarloStats[] = [];
  for (let t = 0; t <= steps; t++) {
    stats.push({
      t,
      Λ_p5: +percentile(LamByT[t], 0.05).toFixed(3),
      Λ_p25: +percentile(LamByT[t], 0.25).toFixed(3),
      Λ_p50: +percentile(LamByT[t], 0.50).toFixed(3),
      Λ_p75: +percentile(LamByT[t], 0.75).toFixed(3),
      Λ_p95: +percentile(LamByT[t], 0.95).toFixed(3),
      Λ_mean: +mean(LamByT[t]).toFixed(3),
      V_p5: +percentile(VbyT[t], 0.05).toFixed(3),
      V_p50: +percentile(VbyT[t], 0.50).toFixed(3),
      V_p95: +percentile(VbyT[t], 0.95).toFixed(3),
      S_p5: +percentile(SbyT[t], 0.05).toFixed(3),
      S_p50: +percentile(SbyT[t], 0.50).toFixed(3),
      S_p95: +percentile(SbyT[t], 0.95).toFixed(3),
      Ω_p50: +percentile(OmegaByT[t], 0.50).toFixed(3),
      Ω_mean: +mean(OmegaByT[t]).toFixed(3),
      pPausa: +(verdictByT[t][0] / N).toFixed(4),
      pEspera: +(verdictByT[t][1] / N).toFixed(4),
      pReconecta: +(verdictByT[t][2] / N).toFixed(4),
      pMonitorea: +(verdictByT[t][3] / N).toFixed(4),
      pActua: +(verdictByT[t][4] / N).toFixed(4),
    });
  }
  
  const Lambda0 = computeLogosAlignment(s).value;
  const pLambdaUp = allFinalLambda.filter(l => l > Lambda0).length / N;
  const finalLamMean = mean(allFinalLambda);
  const finalLamStd = std(allFinalLambda);
  const pNegAttractor = negativeAttractorCount / N;
  
  // Wilson CIs on final verdicts
  const finalVerdicts = verdictByT[steps];
  const verdictCI: Record<string, any> = {};
  verdictNames.forEach((name, i) => {
    const ci = wilsonCI(finalVerdicts[i], N);
    verdictCI[name] = { count: finalVerdicts[i], ...ci };
  });
  
  const dominantIdx = finalVerdicts.indexOf(Math.max(...finalVerdicts));
  const dominantP = finalVerdicts[dominantIdx] / N;
  
  // Time to ACTÚA
  const actuaReached = timeToActua.filter(t => t >= 0);
  const pNeverActua = 1 - actuaReached.length / N;
  
  return {
    stats,
    N,
    steps,
    sigma,
    pLambdaUp,
    finalLamMean,
    finalLamStd,
    pNegAttractor,
    verdictCI,
    verdictNames,
    verdictColors,
    dominantVerdict: verdictNames[dominantIdx],
    dominantP,
    pNeverActua,
    ttaMedian: actuaReached.length > 0 ? +percentile(actuaReached, 0.50).toFixed(1) : null,
  };
}

/**
 * Project deterministic trajectory (for visualization)
 */
export function projectTrajectory(
  s: ConsciousnessState, 
  steps: number = 30
): Array<{ t: number; V: number; S: number; Λ: number; Ω: number; C: number }> {
  const data: any[] = [];
  let cur = { ...s };
  
  for (let t = 0; t <= steps; t++) {
    const lam = computeLogosAlignment(cur).value;
    const V = computeViability(cur, lam).value;
    const S_val = computeEntropy(cur, lam).value;
    const C_val = computeCoherence(cur, lam).coherence;
    const F_val = computeFreeEnergy(cur, lam).value;
    const Omega = computeOmega(lam, V, S_val, C_val, F_val).value;
    
    data.push({ 
      t, 
      V: +V.toFixed(3), 
      S: +S_val.toFixed(3), 
      Λ: +lam.toFixed(3), 
      Ω: +Omega.toFixed(3), 
      C: +C_val.toFixed(3) 
    });
    
    // Apply small drift for next step
    for (const k of Object.keys(cur)) {
      const val = (cur as any)[k];
      const drift = (val > 0.5 ? -0.003 : 0.003) + (Math.random() - 0.48) * 0.01;
      (cur as any)[k] = clamp(val + drift);
    }
  }
  
  return data;
}
